import Component from '@glimmer/component';
import { service } from '@ember/service';
import { on } from '@ember/modifier';
import { array, hash, concat } from '@ember/helper';
import { t } from 'ember-intl';
import {
  HdsPageHeader,
  HdsDropdown,
  HdsFormTextInputField,
  HdsTable,
  HdsLinkInline,
  HdsPaginationCompact,
} from '@hashicorp/design-system-components/components';
import { CoreLayoutMain, BreadcrumbsContainer, Flex } from 'core/components';
import { boxMargin } from 'core/modifiers';
import linkToTfcResource from 'core/helpers/link-to-tfc-resource';

/**
 * The current paginated list of workspaces.
 * @argument workspaces
 * @type {Array}
 */

/**
 * The current paginated list's page metadata.
 * @argument pagination
 * @type {Object}
 */

/**
 * A callback to set the page size
 * @argument onPageSizeChange
 * @type {Function}
 */

/**
 * The current in-scope organization's ID
 * @argument orgId | projectId
 * @type {string}
 */

/**
 * The current in-scope project's ID
 * @argument projectId
 * @type {string}
 */
/**
 *
 * Displays the list of workspaces for the current project.
 *
 *
 * ```
 * <Page::Terraform::Workspaces::List
 * />
 * ```
 *
 * @class PageTerraformWorkspacesList
 *
 */
export default class PageTerraformWorkspacesList extends Component {
  @service router;

  query;

  get paginationQueryFunction() {
    const { nextPageToken, previousPageToken } = this.args?.pagination ?? {};
    return (page) => {
      if (page === 'next') {
        return {
          next: nextPageToken,
          prev: undefined,
        };
      } else {
        return {
          prev: previousPageToken,
          next: undefined,
        };
      }
    };
  }

  onSearchInput = ({ target: { value } }) => {
    this.query = value;

    if (this.query === '') {
      //this.args.onSearch(this.query);
      this.router.transitionTo('terraform.workspaces.list', {
        queryParams: {
          filter: undefined,
        },
      });
    }
  };

  onSearchKeyup = ({ target: { value }, key }) => {
    this.query = value;

    if (key === 'Enter') {
      //this.args.onSearch(this.query);
      this.router.transitionTo('terraform.workspaces.list', {
        queryParams: {
          filter: this.query,
        },
      });
    }
  };

  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          <PH.Title data-test-tfc-workspaces-list-title>
            {{t "components.page.terraform.workspaces.list.title"}}
          </PH.Title>
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          <PH.IconTile @icon="folder" />
          <PH.Description data-test-tfc-workspaces-list-description>
            {{t "components.page.terraform.workspaces.list.description"}}
          </PH.Description>
          <PH.Actions>
            {{!-- {{#if (can "create workspace" @projects)}} --}}
            <HdsDropdown data-test-create-workspace as |dd|>
              <dd.ToggleButton
                @text={{t
                  "components.page.terraform.workspaces.list.create.header"
                }}
              />
              <dd.Interactive
                @href=""
                @text={{t
                  "components.page.terraform.workspaces.list.create.us"
                }}
                @trailingIcon="external-link"
              />
              <dd.Separator />
              <dd.Interactive
                @href=""
                @text={{t
                  "components.page.terraform.workspaces.list.create.eu"
                }}
                @trailingIcon="external-link"
              />
            </HdsDropdown>
            {{!-- {{/if}} --}}
          </PH.Actions>
        </HdsPageHeader>
      </:header>
      <:body>
        <div>
          <HdsFormTextInputField
            {{boxMargin "0 0 lg 0"}}
            @type="search"
            placeholder={{t
              "components.page.terraform.workspaces.list.search-placeholder"
            }}
            @value={{@leafNameFilter}}
            {{on "input" this.onSearchInput}}
            {{on "keyup" this.onSearchKeyup}}
          />
          <HdsTable
            @model={{@workspaces}}
            @columns={{array
              (hash
                isSortable="true"
                key="resourceName"
                label=(t
                  "components.page.terraform.workspaces.list.table.workspaces"
                )
              )
              (hash
                key="description"
                label=(t
                  "components.page.terraform.workspaces.list.table.geography"
                )
              )
              (hash key="options" align="right" width="10%")
            }}
            @valign="middle"
          >
            <:body as |B|>
              <B.Tr data-table-row>
                <B.Td>
                  <HdsLinkInline
                    @icon={{false}}
                    @route="terraform.workspaces.detail"
                    @model={{B.data.resourceId}}
                  >
                    {{B.data.displayName}}
                  </HdsLinkInline>
                </B.Td>
                <B.Td class="hcp-word-wrap-break-all">
                  {{t
                    (concat
                      "components.page.terraform.workspaces.list.table."
                      B.data.geo
                    )
                  }}
                </B.Td>
                <B.Td>
                  <Flex @justifyContent="end">
                    <HdsDropdown
                      data-test-list-item={{B.data.resourceId}}
                      as |dd|
                    >
                      <dd.ToggleIcon
                        @icon="more-horizontal"
                        @text={{t "components.form.more"}}
                        @hasChevron={{false}}
                        data-test-more-menu-trigger={{B.data.resourceId}}
                      />
                      <dd.Interactive
                        @route="terraform.workspaces.detail"
                        @model={{B.data.resourceId}}
                        @text={{t
                          "components.page.terraform.workspaces.list.table.options.view-role-assignments"
                        }}
                        id="view-role-assignments-button-{{B.data.resourceId}}"
                        @icon="user-plus"
                        data-test-edit-button={{B.data.resourceId}}
                      />
                      <dd.Separator />
                      <dd.Description
                        @text={{t
                          "components.page.terraform.workspaces.list.table.options.additional-functionality"
                        }}
                      />
                      <dd.Interactive
                        id="modal-dialog-open-button-{{B.data.resourceId}}"
                        @icon="terraform"
                        @trailingIcon="external-link"
                        @href={{linkToTfcResource
                          B.data.geo
                          "hashicorp.terraform.workspace"
                          @orgId
                          @projectId
                          B.data.resourceId
                        }}
                        @isHrefExternal={{true}}
                        @text={{t
                          "components.page.terraform.workspaces.list.table.options.view-in-terraform"
                        }}
                        data-test-tfc-ext-link={{B.data.resourceId}}
                      />
                    </HdsDropdown>
                  </Flex>
                </B.Td>
              </B.Tr>
            </:body>
          </HdsTable>
          <HdsPaginationCompact
            {{boxMargin "md 0 0 0"}}
            @route="terraform.workspaces.list"
            @queryFunction={{this.paginationQueryFunction}}
            @pageSizes={{array 10 25 50 100}}
            @currentPageSize={{@currentPageSize}}
            @showSizeSelector={{true}}
            @onPageSizeChange={{@onPageSizeChange}}
            @isDisabledPrev={{unless @pagination.previousPageToken true}}
            @isDisabledNext={{unless @pagination.nextPageToken true}}
          />
        </div>
      </:body>
    </CoreLayoutMain>
  </template>
}
